import React, { useState, useEffect, lazy, Suspense, useRef, useCallback } from 'react';
import '@/App.css';
// PR-1 — one print stylesheet for every printable document.
import '@/styles/print.css';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import ProjectRouteNudge from '@/components/project/ProjectRouteNudge';
import { Loader2, Sun, Moon } from 'lucide-react';

// Initialize axios interceptors for multi-tenancy (company_id header)
import './utils/axiosConfig';
// Install the `_kmRefreshWrapped` window.fetch hook alongside the axios
// interceptor.  Shares ONE single-flight refresh with axios so
// concurrent 401s do not fire two POST /api/auth/refresh calls.
import { installKmRefreshWrapped } from './utils/fetchAuthWrapper';
import { isPublicPath } from './utils/publicPaths';
import { isClientRoute } from './utils/clientRoutes';

// Install the fetch hook at module-load time (before any component
// renders and before any raw fetch call fires).  Idempotent.
installKmRefreshWrapped();
import ProfileDialog from '@/components/account/ProfileDialog';
import FixedBottomNav from './components/FixedBottomNav';
import UploadQueueIndicator from './components/upload/UploadQueueIndicator';
import { useKeyboardAwareInputs } from './hooks/useKeyboardAwareInputs';

// Components needed at App level
import QuickStartWizard from './components/QuickStartWizard';

// Auth pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AccountSecurityPage from './pages/AccountSecurityPage';
import SuperAdminPage from './pages/SuperAdminPage';

// Lazy-loaded heavy pages (code splitting for better performance)
const FloorPlanPage = lazy(() => import('./pages/FloorPlanPage'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const AssembliesPage = lazy(() => import('./pages/AssembliesPage'));
const AssemblySheet = lazy(() => import('./pages/AssemblySheet'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AssemblyPublicPage = lazy(() => import('./pages/AssemblyPublicPage'));  // AS-2d
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const ProjectHubFull = lazy(() => import('./pages/ProjectHubFull'));
const WorkTablePage = lazy(() => import('./pages/WorkTablePage'));
const WorkTableHome = lazy(() => import('./pages/WorkTableHome'));
const QRCodeGenerator = lazy(() => import('./pages/QRCodeGenerator'));
const EstimatePage = lazy(() => import('./pages/EstimatePage'));
const DrawingRoom = lazy(() => import('./pages/DrawingRoom'));
const DreamShare = lazy(() => import('./pages/DreamShare'));  // DR-2d — the phone's way in
const ContractorsPage = lazy(() => import('./pages/ContractorsPage'));
const SystemBlueprint = lazy(() => import('./pages/SystemBlueprint'));
const QuickSiteLog = lazy(() => import('./pages/QuickSiteLog'));
const QuickSiteVisitPage = lazy(() => import('./pages/QuickSiteVisitPage'));
const SiteVisitReviewPage = lazy(() => import('./pages/SiteVisitReviewPage'));
const QuickShare = lazy(() => import('./pages/QuickShare'));
const QuickMemoPage = lazy(() => import('./pages/QuickMemoPage'));
const QuickContactsPage = lazy(() => import('./pages/QuickContactsPage'));
const QuickClipPage = lazy(() => import('./pages/QuickClipPage'));
const QuickListsPage = lazy(() => import('./pages/QuickListsPage'));
const HomeScreenPage = lazy(() => import('./pages/HomeScreenPage'));
const QuickLanding = lazy(() => import('./pages/HomeScreenPage').then((m) => ({ default: m.QuickLanding })));
const MobileWidgetDashboard = lazy(() => import('./pages/MobileWidgetDashboard'));
const QuickActionsWidget = lazy(() => import('./pages/QuickActionsWidget'));
const DevNotes = lazy(() => import('./pages/DevNotes'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const PopoutWrapper = lazy(() => import('./pages/PopoutWrapper'));
const ProjectGallery = lazy(() => import('./pages/ProjectGallery'));
const TakeoffMeasureTest = lazy(() => import('./pages/TakeoffMeasureTest'));
const PdfMeasureTool = lazy(() => import('./pages/PdfMeasureTool'));
const LeadDetailPage = lazy(() => import('./pages/LeadDetailPage'));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const PunchListPage = lazy(() => import('./pages/PunchListPage'));
const DecisionLogPage = lazy(() => import('./pages/DecisionLogPage'));
const PurchasesPage = lazy(() => import('./pages/PurchasesPage'));
const ProjectFrame = lazy(() => import('./components/project/ProjectFrame'));
const AdminImportBundle = lazy(() => import('./pages/AdminImportBundle'));
const ProcessMapPage = lazy(() => import('./pages/ProcessMapPage'));
const ProcessMapTemplateEditor = lazy(() => import('./pages/ProcessMapTemplateEditor'));
const TenantFileStandardsEditor = lazy(() => import('./pages/TenantFileStandardsEditor'));

// Widget pages for PWA home screen shortcuts
const CalendarWidget = lazy(() => import('./pages/widgets/CalendarWidget'));
const TasksWidget = lazy(() => import('./pages/widgets/TasksWidget'));
const NotesWidget = lazy(() => import('./pages/widgets/NotesWidget'));
const FocusWidget = lazy(() => import('./pages/widgets/FocusWidget'));
const LegalPage = lazy(() => import('./pages/LegalPage'));

// Regular imports (smaller pages)
import Dashboard from './pages/Dashboard';
import ResourcesPage from './pages/ResourcesPage';
import PipelinePage from './pages/PipelinePage';
import NotesPage from './pages/NotesPage';
import ClipDetail from './pages/ClipDetail';
import ClipsLibrary from './pages/ClipsLibrary';
import ContactsPage from './pages/ContactsPage';
import ContactReviewQueuePage from './pages/ContactReviewQueuePage';
import MoodBoardPage from './pages/MoodBoardPage';
import CompanySettings from './pages/CompanySettings';
import CornerOfDesk from './pages/CornerOfDesk';
import MyDeskPlanRoom from './pages/MyDeskPlanRoom';
import GlobalFooter from './components/GlobalFooter';
import DocumentLibrary from './pages/DocumentLibrary';
// DP269-A — the approved Site Office. The legacy SiteOffice.js keeps its
// file but loses its route; it is retired in a later package.
import SiteOfficePage from './pages/SiteOfficePage';
import SiteOfficePunchItem from './pages/SiteOfficePunchItem';
import SiteOfficePhotos from './pages/SiteOfficePhotos';
import SiteOfficeSpecs from './pages/SiteOfficeSpecs';
import SiteOfficeSpecItem from './pages/SiteOfficeSpecItem';
// RS-2 — the Questions door: a contractor asks, the firm answers.
import OfficeQuestions from './pages/site-office/OfficeQuestions';
import OfficeSubmittals from './pages/site-office/OfficeSubmittals';
import FieldSheetPage from './pages/FieldSheetPage';
import MaterialTagPage from './pages/MaterialTagPage';
import PalettePublicPage from './pages/PalettePublicPage';  // RS-3a
import QuestionAnswerPage from './pages/QuestionAnswerPage';  // RS-8
import PublicSpecSheet from './pages/PublicSpecSheet';
import PublicPlanReview from './pages/PublicPlanReview';  // SC-5
import DocumentGenerator from './pages/DocumentGenerator';
import DocumentEditor from './pages/DocumentEditor';
import ContractorAgreementEditor from './pages/ContractorAgreementEditor';
import NotificationsPage from './pages/NotificationsPage';
import ScopeStatementEditor from './pages/ScopeStatementEditor';
import ClientSurveyPage from './pages/ClientSurveyPage';
import SurveyTemplatesPage from './pages/SurveyTemplatesPage';
import ScheduledEmailsPage from './pages/ScheduledEmailsPage';
import TermsGlossary from './pages/TermsGlossary';
import CalculatorPanel from './components/CalculatorPanel';
import PhaseTemplateEditor from './pages/PhaseTemplateEditor';
import FinancialsPage from './pages/FinancialsPage';
import InsuranceAuditPage from './pages/InsuranceAuditPage';
import BudgetTracker from './pages/BudgetTracker';
import InvoicesPage from './pages/InvoicesPage';
import VendorsPage from './pages/VendorsPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import ProcurementDashboard from './pages/ProcurementDashboard';
import SiteTagsPage from './pages/SiteTagsPage';
import SiteTagsWidget from './pages/widgets/SiteTagsWidget';
import SiteTagPage from './pages/SiteTagPage';
import ChangeOrdersPage from './pages/ChangeOrdersPage';
import DrawRequestsPage from './pages/DrawRequestsPage';
import ReceivedInvoicesPage from './pages/ReceivedInvoicesPage';
import RFPsPage from './pages/RFPsPage';
import ProjectProcurement from './pages/ProjectProcurement';
import BlueprintLibrary from './pages/BlueprintLibrary';
import ContractorPortal from './pages/ContractorPortal';
import DesignCompanionPage from './pages/DesignCompanionPage';
import JoinPage from './pages/JoinPage';
import TeamPage from './pages/TeamPage';
import RolePermissionsMatrix from './pages/RolePermissionsMatrix';
import AdminDiagnostics from './pages/AdminDiagnostics';
import SitePhotoRecovery from './pages/SitePhotoRecovery';
import Rulebook from './pages/Rulebook';
import AllActivityPage from './pages/AllActivityPage';
import PaintLibrary from './pages/PaintLibrary';
import DrawingControlPage from './pages/DrawingControlPage';
import RFISubmittalPage from './pages/RFISubmittalPage';
import BidPackagesPage from './pages/BidPackagesPage';
import AskForPricePage from './pages/AskForPricePage';
import ScopeBuilderPage from './pages/ScopeBuilderPage';
import ScheduleBuilderPage from './pages/ScheduleBuilderPage';
import PublicSchedulePage from './pages/PublicSchedulePage';
import QualityControlPage from './pages/QualityControlPage';
import WarrantyTrackerPage from './pages/WarrantyTrackerPage';
import PermitInspectionPage from './pages/PermitInspectionPage';
import LienWaiverPage from './pages/LienWaiverPage';
// CRMPage removed - now using unified PipelinePage
import { Toaster } from '@/components/ui/sonner';
import { useNavigationHistory } from './components/BackButton';
import AiChad from './components/ai-chad/AiChad';
import { BlueprintProvider } from './contexts/BlueprintContext';
import { TourProvider } from './contexts/TourContext';
import { HelpIconProvider } from './contexts/HelpIconContext';
import { FocusSessionProvider } from './contexts/FocusSessionContext';
import { RoleProvider } from './contexts/RoleContext';
import { WatermarkProvider } from './components/tools/shared/WatermarkSettings';
import LibraryHub from './components/LibraryHub';
import TaskFloat from './components/tasks/TaskFloat';
import BlueprintViewer from './components/Blueprint/BlueprintViewer';
import GlobalBlueprintToggle from './components/Blueprint/GlobalBlueprintToggle';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import CompassDrawer from './components/CompassDrawer';
import FloatingTabGroup from './components/FloatingTabGroup';
import PortfolioViewer from './pages/PortfolioViewer';
import PhotoShareViewer from './pages/PhotoShareViewer';
import MemoShareViewer from './pages/MemoShareViewer';
// DP268-E — /agreement/:token. Imported eagerly like the other login-free
// portal pages above (SiteOffice, PhotoShareViewer, MemoShareViewer); the
// brief said "lazy, as the other portal pages are loaded" and the other
// portal pages are not lazy, so this follows them rather than the wording.
import AgreementPage from './pages/AgreementPage';
import BuildVersionWatcher from './components/BuildVersionWatcher';
import axios from 'axios';
import { QUICK_ACTIONS } from './data/quickActions';
// GuidedTour removed - now using InteractiveTutorial system

// DP261 — a deploy replaces the content-hashed chunk files, so a tab that has
// been open across one answers a lazy import with "Loading chunk 913 failed".
// That is not a crash to report, it is a stale shell to replace: clear the
// worker and every cache, ask for index.html past the HTTP cache, and reload —
// once per build per tab, so a genuine chunk fault cannot become a loop.
const CHUNK_FLAG = 'km-chunk-reload';

const isChunkError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /Loading (?:CSS )?chunk \d+ failed/i.test(error.message || '');
};

export async function recoverFromStaleBuild() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) { /* blocked or unsupported: the reload still helps */ }
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (e) { /* as above */ }
  try {
    // Hard failure only, never on a healthy load: the edge caches `/` for
    // five minutes, so the reload below needs the new document to exist.
    await fetch(`/index.html?km-bust=${Date.now()}`, { cache: 'reload' });
  } catch (e) { /* offline: reload will serve what it can */ }
  window.location.reload();
}

// Global Error Boundary — prevents full blank screen on crashes
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('App crash caught by ErrorBoundary:', error, info);
    if (!isChunkError(error)) return;
    const key = `${CHUNK_FLAG}:${window.__KEYMADE_BOOT_FP || 'unknown'}`;
    let already = false;
    try { already = sessionStorage.getItem(key) === '1'; } catch (e) { already = false; }
    if (already) return;   // one automatic reload per build per tab
    try { sessionStorage.setItem(key, '1'); } catch (e) { /* private mode */ }
    recoverFromStaleBuild();
  }
  render() {
    if (this.state.hasError) {
      const stale = isChunkError(this.state.error);
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f5f0', fontFamily: 'system-ui' }}>
          <div style={{ textAlign: 'center', maxWidth: 420, padding: 32 }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>
              {stale ? 'A newer version is on the server' : 'Something went wrong'}
            </h2>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
              {stale
                ? 'This tab is running an older build. Reload to pick up the current one.'
                : (this.state.error?.message || 'An unexpected error occurred.')}
            </p>
            <button
              onClick={() => {
                if (stale) { recoverFromStaleBuild(); return; }
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              style={{ background: '#BC5D41', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
              data-testid={stale ? 'error-boundary-reload-btn' : 'error-boundary-reset-btn'}>
              {stale ? 'Reload' : 'Back to Dashboard'}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Page loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0D0D0D]">
    <div className="text-center">
      <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
      <p className="text-gray-500 dark:text-gray-400">Loading page...</p>
    </div>
  </div>
);

// Navigation tracker component
function NavigationTracker() {
  useNavigationHistory();
  return null;
}

// PS-3g2 — a drawer row opens a global tool: send its event, then go back so it opens over the page you were on.
function ToolOpener({ event }) {
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    window.dispatchEvent(new CustomEvent(event));
    if (location.key === 'default') navigate('/dashboard', { replace: true }); else navigate(-1);
  }, []);
  return null;
}

// PS-3i/PS-3f3 — every quick screen's own icon and name, read by Safari at "Add to Home Screen".
const QUICK_NAMES = Object.fromEntries([...QUICK_ACTIONS, { id: 'lists', label: 'Lists' }].map((a) => [a.id, a.label]));
function QuickScreenMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const id = (pathname.match(/^\/quick\/([^/]+)/) || [])[1];
    const name = id && QUICK_NAMES[id];
    if (!name) return undefined;
    const link = document.querySelector('link[rel="apple-touch-icon"]');
    const prevTitle = document.title;
    const want = `KEY-MADE · ${name}`;
    if (link) link.setAttribute('href', `/icons/quick/${id}-192.png`);
    document.title = want;
    // The unread count rewrites the title on its own clock; a quick screen keeps its name.
    const titleEl = document.querySelector('title');
    const keep = titleEl ? new MutationObserver(() => { if (document.title !== want) document.title = want; }) : null;
    if (keep) keep.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => {
      if (keep) keep.disconnect();
      if (link) link.setAttribute('href', '/icons/icon-192x192.png');
      document.title = prevTitle;
    };
  }, [pathname]);
  return null;
}

// Public-path list lives in utils/publicPaths so axiosConfig + this gate
// stay in sync. Adding a new public route? Update utils/publicPaths.js.

/**
 * Top-level auth gate. Renders children when:
 *   - current route is in the public list (no JWT needed), OR
 *   - an accessToken exists in localStorage AND a one-shot /api/auth/me probe
 *     confirms the token is still valid AND the user still exists.
 *
 * On /me 401 → tries the existing axios refresh flow; on hard fail → clears
 * stale localStorage state and redirects to /login.
 * On /me 404 (user deleted) → clears state + redirects to /login.
 */
function PublicOrAuthGate({ children }) {
  const location = useLocation();
  const [meChecked, setMeChecked] = useState(false);
  const [meValid, setMeValid] = useState(true);
  const publicPath = isPublicPath(location.pathname);
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  useEffect(() => {
    // Skip /me check on public routes (no token expected) or when there is no token.
    if (publicPath || !token) {
      setMeChecked(true);
      return;
    }
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    // Single 401 retry with a short delay — absorbs the transient
    // post-login backend-state race that caused "first login lands
    // back on /login silently". The LoginPage already verifies before
    // navigating, so by the time we get here a real 401 is almost
    // certainly a stale token; but the retry protects every other
    // code path that lands on a protected route (refresh, deep link,
    // tab restore) without re-issuing the verification cost.
    const fetchMe = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const res = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          _skipAuthRedirect: true,
          validateStatus: () => true,
        });
        if (res.status !== 401) return res;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 300));
      }
      // Both attempts 401'd — return the last one (callers branch on status)
      return { status: 401, data: null };
    };
    fetchMe()
      .then((res) => {
        if (cancelled) return;
        if (res.status >= 200 && res.status < 300) {
          // Refresh in-memory user info from the authoritative source.
          try {
            const data = res.data || {};
            const user = data?.user || {};
            const company = data?.company || {};
            if (user.id) localStorage.setItem('userId', user.id);
            if (user.email) localStorage.setItem('userEmail', user.email);
            if (user.role) localStorage.setItem('userRole', user.role);
            const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
            if (fullName) localStorage.setItem('userName', fullName);
            if (company.id) localStorage.setItem('companyId', company.id);
            if (company.name) localStorage.setItem('companyName', company.name);
            if (company.slug) localStorage.setItem('companySlug', company.slug);
          } catch (_) { /* ignore parse errors */ }
          setMeValid(true);
        } else if (res.status === 401 || res.status === 404) {
          // Token genuinely stale or user gone (after retry). Clear and redirect.
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tokenExpiresAt');
          setMeValid(false);
        } else {
          // 5xx / network — allow the app to render; surface errors will appear on data fetches.
          setMeValid(true);
        }
        setMeChecked(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Network failure → don't aggressively log the user out; let them retry.
        setMeChecked(true);
      });
    return () => { cancelled = true; };
  }, [publicPath, token]);

  if (publicPath) return children;
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!meChecked) {
    // Brief loading state while /me round-trips; matches the Suspense fallback.
    return <PageLoader />;
  }
  if (!meValid) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// Hide global app chrome on standalone pages (legal, contractor portal, etc.)
function HideOnLegalPages({ children }) {
  const location = useLocation();
  // SV-1 — site capture is a one-hand screen held at arm's length on a wall.
  // None of the shell's floaters belong on it.
  const isStandalone = isClientRoute(location.pathname) || location.pathname.startsWith('/quick/site-visit') || location.pathname.startsWith('/legal/') || location.pathname.startsWith('/contractor-portal') || location.pathname.startsWith('/photo-shares/') || location.pathname.startsWith('/memo/') || location.pathname.startsWith('/agreement/') || location.pathname.startsWith('/f/') || location.pathname.startsWith('/m/') || location.pathname.startsWith('/p/') || location.pathname.startsWith('/q/') || location.pathname.startsWith('/a/') || location.pathname.startsWith('/b/') || (location.pathname.startsWith('/work-tables/shared/') && new URLSearchParams(location.search).has('portal'));
  if (isStandalone) return null;
  return <>{children}</>;
}

// AI Chad wrapper — reads current route and project from URL.
//
// Route suppression rules (2026-06-02, floating-Chad layout overlap fix):
//   Layout A (banner pages — mount <AIChadGreeting>): suppress floating Chad
//     to avoid two AI Chad surfaces on the same screen.
//     /, /dashboard      — UserDashboard (My Desk)
//     /projects          — Dashboard (All Projects)
//     /projects/:id/desk — ProjectHubFull (project landing, e.g. Skyfall)
//   Layout D (auth pages — pre-tenant): suppress floating Chad — no
//     authenticated tenant context exists yet so Chad can't be useful.
//     /login, /register, /forgot-password, /reset-password
//   Layout B (non-banner authenticated pages): floating Chad PRESENT —
//     only AI Chad surface on those routes.
//   Layout C (portals: SiteOffice, DrawingRoom, ContractorPortal,
//     PortfolioViewer): floating Chad PRESENT — per-tenant AI Chad access
//     for portal users. Per-user-class context filtering is Phase F work
//     (deferred) — until then floating Chad has limited utility on portals
//     but suppression is the wrong direction. See PRD AI Chad architecture.
function _isAiChadSuppressedRoute(pathname, phone = false) {
  if (!pathname) return false;
  // DP263 — a shared photo page is read by someone with no account. No
  // assistant, no app furniture: the photo and who sent it, nothing else.
  // SV-1 — no assistant over the capture screen.
  if (pathname.startsWith('/quick/site-visit')) return true;
  // DR-1 — a client's page carries nothing of the staff app.
  if (isClientRoute(pathname)) return true;
  if (pathname.startsWith('/photo-shares/') || pathname.startsWith('/memo/')
      || pathname.startsWith('/agreement/')
      || pathname.startsWith('/f/') || pathname.startsWith('/m/')
      || pathname.startsWith('/b/') || pathname.startsWith('/a/')
      || pathname.startsWith('/p/')
      // RS-8 — a vendor's answer page carries nothing of the staff app.
      || pathname.startsWith('/q/')) {
    return true;
  }
  // Layout D — auth/pre-tenant pages. Exact match.
  if (pathname === '/login' || pathname === '/register'
      || pathname === '/forgot-password' || pathname === '/reset-password') {
    return true;
  }
  // Layout A — banner pages that mount <AIChadGreeting>.
  // PH-1 — not on a phone: there the header's Chad button opens his panel on every page.
  if (phone) return false;
  if (pathname === '/' || pathname === '/dashboard' || pathname === '/projects') {
    return true;
  }
  // /projects/:id/desk → ProjectHubFull. Suppress only the /desk landing,
  // not the project sub-pages (those are Layout B).
  if (pathname.startsWith('/projects/') && pathname.endsWith('/desk')) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 3) return true;
  }
  return false;
}

// Global bottom-left profile menu (logout discoverability — 2026-06):
// mounts FixedBottomNav on every authenticated staff page that doesn't
// already render its own instance (My Desk and Project Hub pass
// page-specific profile/role handlers there). Public paths and portal
// identities (no staff accessToken) get nothing.
function GlobalFixedNav({ darkMode }) {
  const location = useLocation();
  if (!hasGlobalFixedNav(location.pathname)) return null;
  return (
    <FixedBottomNav
      darkMode={darkMode}
      currentUser={{ name: localStorage.getItem('userName') || '' }}
    />
  );
}

function AiChadWrapper({ darkMode }) {
  const location = useLocation();
  const currentRoute = location.pathname;
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setPhone(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  if (_isAiChadSuppressedRoute(currentRoute, phone)) {
    return null;
  }
  const parts = currentRoute.split('/').filter(Boolean);
  let activeProjectId = null;
  if (parts[0] === 'projects' && parts.length >= 2) activeProjectId = parts[1];
  else if (parts[0] === 'work-tables' && parts.length >= 2) activeProjectId = parts[1];
  return <AiChad currentRoute={currentRoute} activeProjectId={activeProjectId} darkMode={darkMode} />;
}

// Blueprint route tracker - updates context when route changes
function BlueprintRouteTracker() {
  const location = useLocation();
  
  // We need to use a ref-based approach since this component is inside the provider
  useEffect(() => {
    // Dispatch a custom event that the BlueprintContext can listen to
    const event = new CustomEvent('blueprint-route-change', { 
      detail: { route: location.pathname + location.search }
    });
    window.dispatchEvent(event);
  }, [location]);
  
  return null;
}

// True when GlobalFixedNav (bottom-left profile/logout menu) is mounted on
// this path — used to keep other bottom-left floaters from covering it.
const hasGlobalFixedNav = (path) =>
  !!localStorage.getItem('accessToken') &&
  !isPublicPath(path) &&
  // DR-1 — the staff badge is staff furniture: not on a client's page.
  !isClientRoute(path) &&
  // DP269-A3 — the staff "Open as …" preview is the party's page; the badge
  // that belongs to the app shell does not belong on it.
  !/^\/projects\/[^/]+\/site-office\//.test(path) &&
  // SV-1 — the avatar menu (and the Help & Guides book inside it) sits exactly
  // where the capture bar's buttons are.
  !path.startsWith('/quick/site-visit') &&
  !(path === '/' || path === '/dashboard' || /^\/projects\/[^/]+\/hub/.test(path));

// Global Dark Mode Toggle - visible on non-dashboard, non-worktable, non-project pages
function GlobalDarkModeToggle({ darkMode, onToggle }) {
  const location = useLocation();
  // Hidden where the page still has a toggle of its own: the Yard
  // (/work-tables/:id, toggle in its header) and project pages.
  // DP60 Part 0c SUPERSEDES DP60 Part 1a. Part 1a took /dashboard and /projects
  // OFF this list because neither had a toggle. Part 0c then gave the shell a
  // FIXED FOOT that carries dark mode as a rail box on all three shell pages
  // (rail-dark-mode-toggle), so this floating one would now be a SECOND toggle
  // there. Both shell pages, and /work-table, are back on the list — for the
  // opposite reason to the original: they have a toggle again, in the rail.
  // DR-5b — a /widget/ page is a window of its own (TK-15): no shell furniture
  // belongs over it, and this button's z-[9997] sat above every token.
  if (location.pathname.startsWith('/widget/')) return null;
  if (location.pathname === '/') return null;
  if (location.pathname === '/dashboard') return null;
  if (location.pathname === '/work-table') return null;
  if (location.pathname.startsWith('/work-tables/')) return null;
  if (location.pathname.startsWith('/project')) return null;
  if (location.pathname.startsWith('/consultation/')) return null;

  // DP161 — "above the avatar" was 4.25rem, which was not enough: the toggle's box
  // ran from y1156 to y1196 and the avatar's began at y1185, so this button's
  // z-index put it across the avatar's face on every page carrying the nav. The
  // avatar column is 180px wide at left-3, so the toggle now stands CLEAR OF IT
  // horizontally instead of stacking above it. Below md the nav is hidden
  // (`hidden md:flex`), so there the corner is the toggle's own again.
  const bottomClass = hasGlobalFixedNav(location.pathname) ? 'bottom-4 left-4 md:left-52' : 'bottom-4 left-4';
  
  return (
    <button
      onClick={onToggle}
      title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      className={`fixed ${bottomClass} z-[9997] w-10 h-10 rounded-full hidden sm:flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 ${
        darkMode 
          ? 'bg-[#1A1A1A] border border-[#2A2A2A] text-amber-400 hover:bg-[#2A2A2A]' 
          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
      data-testid="global-dark-mode-toggle"
    >
      {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </button>
  );
}

// Redirect legacy project route to Project Info (the new landing)
function ProjectRedirect() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'project';
  return <Navigate to={`/projects/${projectId}/desk?tab=${tab}`} replace />;
}

/**
 * HubLegacyRedirect catches /projects/:id/hub URLs and forwards to /desk.
 *
 * As of Cleanup 3: ~131 hardcoded /hub URLs remain across ~47 frontend files
 * (FinancialRoadmap, CompassDrawer, ProjectJourney, NotificationsPanel, etc.).
 * Mass-sweeping is too risky for marginal benefit — they all redirect cleanly
 * via this component.
 *
 * Pattern: when touching any of these files for other reasons, update its
 * /hub URLs to /desk?tab=... directly as a side concern.
 *
 * This redirect stays in place permanently OR until the URLs are organically
 * cleaned up across the codebase.
 */
function HubLegacyRedirect() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'project';
  return <Navigate to={`/projects/${projectId}/desk?tab=${tab}`} replace />;
}

// Wrapper to force remount PurchaseOrdersPage when projectId changes
// This fixes the blank screen bug on first navigation
function PurchaseOrdersPageWrapper() {
  const { projectId } = useParams();
  return <PurchaseOrdersPage key={projectId || 'global'} />;
}

// MD-2 — AI Chad can take you to the page: his [[go:PATH|…]] links raise
// 'keymade:chad-go' and this one listener, inside the Router, navigates.
function ChadGoListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const onGo = (e) => {
      const path = e?.detail?.path;
      if (typeof path === 'string' && path.startsWith('/') && !path.includes('://')) navigate(path);
    };
    window.addEventListener('keymade:chad-go', onGo);
    return () => window.removeEventListener('keymade:chad-go', onGo);
  }, [navigate]);
  return null;
}

function App() {
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [compassOpen, setCompassOpen] = useState(false);
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const [libraryHubOpen, setLibraryHubOpen] = useState(false);
  // LH-2 — a picker can open the drawer to choose: {mode, label}.
  const [libraryHubAsk, setLibraryHubAsk] = useState({ mode: 'browse', label: '' });

  // Stage 2D Part 4b (2026-06-15) — keep focused inputs visible above
  // the on-screen keyboard on mobile (visualViewport + scrollIntoView).
  useKeyboardAwareInputs();
  
  // Initialize dark mode from localStorage on app mount
  useEffect(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    if (saved === 'true') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Global keyboard shortcut for calculator (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCalculatorOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Listen for compass state changes from CompassDrawer
  useEffect(() => {
    const handleCompassChange = (e) => {
      setCompassOpen(e.detail?.isOpen ?? false);
    };
    window.addEventListener('compass-state-change', handleCompassChange);
    return () => window.removeEventListener('compass-state-change', handleCompassChange);
  }, []);

  // PS-3g2 — the phone reaches Calculator and Quick Start from the rail drawer.
  useEffect(() => {
    const calc = () => setCalculatorOpen(true);
    const start = () => setQuickStartOpen(true);
    window.addEventListener('km:open-calculator', calc);
    window.addEventListener('km:open-quickstart', start);
    return () => { window.removeEventListener('km:open-calculator', calc); window.removeEventListener('km:open-quickstart', start); };
  }, []);

  // Listen for quickstart events from other components (e.g. UserDashboard button)
  useEffect(() => {
    const openQuickStart = () => setQuickStartOpen(true);
    const toggleLibrary = () => {
      setLibraryHubAsk({ mode: 'browse', label: '' });
      setLibraryHubOpen(prev => !prev);
    };
    const openLibrary = (event) => {
      const ask = event?.detail || {};
      setLibraryHubAsk({ mode: ask.purpose === 'pick' ? 'pick' : 'browse',
                         label: ask.label || '' });
      setLibraryHubOpen(true);
    };
    window.addEventListener('open-quickstart-wizard', openQuickStart);
    window.addEventListener('toggle-library-hub', toggleLibrary);
    window.addEventListener('open-library-hub', openLibrary);
    return () => {
      window.removeEventListener('open-quickstart-wizard', openQuickStart);
      window.removeEventListener('toggle-library-hub', toggleLibrary);
      window.removeEventListener('open-library-hub', openLibrary);
    };
  }, []);
  
  // Handlers for floating tab group
  const handleNewLead = () => {
    // Navigate directly to pipeline with new-lead action (works from any page)
    window.location.href = '/pipeline?action=new-lead';
  };
  
  const handleQuickStart = () => {
    setQuickStartOpen(true);
  };
  
  const handleCompassClick = () => {
    // Dispatch event to toggle compass drawer
    window.dispatchEvent(new CustomEvent('toggle-compass'));
  };
  
  const handleCalcClick = () => {
    setCalculatorOpen(true);
  };

  // Global dark mode toggle function
  const toggleGlobalDarkMode = useCallback(() => {
    const newMode = !document.documentElement.classList.contains('dark');
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('keyMadeDarkMode', newMode.toString());
    setDarkMode(newMode);
  }, []);
  
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });
  
  // Listen for dark mode changes from UserDashboard
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  return (
    <AppErrorBoundary>
    <RoleProvider>
    <WatermarkProvider>
    <BlueprintProvider>
    <HelpIconProvider>
    <FocusSessionProvider>
      <div className="App">
        <BrowserRouter>
          <TourProvider>
          <NavigationTracker />
          <QuickScreenMeta />
          <BlueprintRouteTracker />
          <HideOnLegalPages>
            <GlobalDarkModeToggle darkMode={darkMode} onToggle={toggleGlobalDarkMode} />
          
            {/* Compass Drawer - Hidden tab, controlled via events */}
            <CompassDrawer hideTab={true} />
          
            {/* Unified Floating Tab Group - All 4 tabs move together */}
            <FloatingTabGroup
              onCalcClick={handleCalcClick}
              onCompassClick={handleCompassClick}
              onNewLeadClick={handleNewLead}
              onQuickStartClick={handleQuickStart}
              calcOpen={calculatorOpen}
              compassOpen={compassOpen}
              darkMode={darkMode}
            />
          
            {/* Calculator Panel */}
            <CalculatorPanel 
              isOpen={calculatorOpen} 
              onClose={() => setCalculatorOpen(false)}
              darkMode={darkMode}
            />

            {/* Quick Start Wizard - Global, works from any page */}
            <QuickStartWizard
              open={quickStartOpen}
              onClose={() => setQuickStartOpen(false)}
              onProjectCreated={(project) => {
                // Refresh page data if on dashboard
                window.dispatchEvent(new CustomEvent('project-created', { detail: project }));
              }}
            />
            
            {/* Library Hub - Global drawer accessible from any page */}
            <LibraryHub
              isOpen={libraryHubOpen}
              onClose={() => setLibraryHubOpen(false)}
              mode={libraryHubAsk.mode}
              label={libraryHubAsk.label}
              onItemSelect={(item) => {
                window.dispatchEvent(new CustomEvent('library-item-selected', { detail: item }));
              }}
            />
            <TaskFloat />
          </HideOnLegalPages>
          <ChadGoListener />
          
          <Suspense fallback={<PageLoader />}>
          <PublicOrAuthGate>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/super-admin" element={<SuperAdminPage />} />
            
            {/* Main Dashboard - User's home page (DEFAULT) */}
            <Route path="/" element={<UserDashboard />} />
            <Route path="/dashboard" element={<UserDashboard />} />
            
            {/* Projects listing (legacy route) */}
            <Route path="/projects" element={<Dashboard />} />
            <Route path="/project" element={<Navigate to="/projects" replace />} />
            
            {/* AS-1 — Assemblies: the firm's catalogue of build methods */}
            <Route path="/assemblies" element={<AssembliesPage />} />
            {/* AS-2a — :id is declared before /new so the nav manifest credits
                `/assemblies/${id}` to the sheet; React Router still ranks the
                literal /assemblies/new above the dynamic route. */}
            <Route path="/assemblies/:id" element={<AssemblySheet />} />
            <Route path="/assemblies/new" element={<AssembliesPage startNew />} />

            {/* Admin & Resources */}
            <Route path="/admin" element={<AdminSettings />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/settings/admin" element={<AdminSettings />} />
            <Route path="/settings/company" element={<CompanySettings />} />
            <Route path="/settings/account" element={<AccountSecurityPage />} />
            <Route path="/settings/home-screen" element={<HomeScreenPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/documents" element={<DocumentLibrary />} />
            <Route path="/documents/editor/new" element={<DocumentEditor />} />
            <Route path="/documents/editor/:templateId" element={<DocumentEditor />} />
            <Route path="/documents/contractor-agreement" element={<ContractorAgreementEditor />} />
            <Route path="/documents/contractor-agreement/new" element={<ContractorAgreementEditor />} />
            <Route path="/documents/contractor-agreement/:templateId" element={<ContractorAgreementEditor />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            
            {/* Team Management */}
            <Route path="/team" element={<TeamPage />} />
            <Route path="/admin/roles" element={<RolePermissionsMatrix />} />
            <Route path="/admin/diagnostics" element={<AdminDiagnostics />} />
            <Route path="/admin/site-photo-recovery" element={<SitePhotoRecovery />} />
            <Route path="/admin/rulebook" element={<Rulebook />} />
            <Route path="/admin/import-bundle" element={<AdminImportBundle />} />
            <Route path="/join" element={<JoinPage />} />
            
            {/* All Activity Page */}
            <Route path="/all-activity" element={<AllActivityPage />} />
            
            {/* Paint Library */}
            <Route path="/paint-library" element={<PaintLibrary />} />
            
            {/* Blueprint Library */}
            <Route path="/blueprints" element={<BlueprintLibrary />} />
            <Route path="/glossary" element={<TermsGlossary />} />
            
            {/* Learning Center / Guide - User-facing System Map */}
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/learn" element={<Navigate to="/guide" replace />} />
            
            {/* System Blueprint - Admin Architecture Map */}
            <Route path="/admin/system-blueprint" element={<SystemBlueprint />} />
            
            {/* Phase Template Editor - Admin */}
            <Route path="/admin/phase-templates" element={<PhaseTemplateEditor />} />
            <Route path="/admin/plan-set-template" element={<ProcessMapTemplateEditor />} />
            <Route path="/admin/tenant-file-standards" element={<TenantFileStandardsEditor />} />
          
          {/* Quick Access Routes - PWA Shortcuts */}
          <Route path="/quick/site-log" element={<QuickSiteLog />} />
          <Route path="/quick/site-visit" element={<QuickSiteVisitPage />} />
          <Route path="/quick/site-visit/:project_id" element={<QuickSiteVisitPage />} />
          <Route path="/quick/site-visit/:project_id/:visit_id" element={<QuickSiteVisitPage />} />
          <Route path="/projects/:project_id/site-visits/:visit_id/review" element={<SiteVisitReviewPage />} />
          <Route path="/quick/share" element={<QuickShare />} />
          <Route path="/quick/dashboard" element={<MobileWidgetDashboard />} />
          <Route path="/quick/actions" element={<QuickActionsWidget />} />
          <Route path="/quick/memo" element={<QuickMemoPage />} />
          <Route path="/quick/contacts" element={<QuickContactsPage />} />
          <Route path="/quick/clip" element={<QuickClipPage />} />
          <Route path="/tools/calculator" element={<ToolOpener event="km:open-calculator" />} />
          <Route path="/tools/quick-start" element={<ToolOpener event="km:open-quickstart" />} />
          <Route path="/quick/lists" element={<QuickListsPage />} />
          {['scan', 'expense', 'punch-item', 'meeting', 'plans'].map((id) => <Route key={id} path={`/quick/${id}`} element={<QuickLanding />} />)}
          
          {/* Dev Notes */}
          <Route path="/dev-notes" element={<DevNotes />} />
          
          {/* Vault */}
          <Route path="/vault" element={<VaultPage />} />
          
          {/* Mobile-Optimized Punch List */}
          
          {/* Decision Log */}
          
          {/* Takeoff Measure Test Lab */}
          <Route path="/takeoff-test" element={<TakeoffMeasureTest />} />
          <Route path="/pdf-measure" element={<PdfMeasureTool />} />
          
          {/* Widget Pages - Phone Home Screen Replacements */}
          <Route path="/widget/calendar" element={<CalendarWidget />} />
          <Route path="/widget/tasks" element={<TasksWidget />} />
          <Route path="/widget/notes" element={<NotesWidget />} />
          <Route path="/widget/focus" element={<FocusWidget />} />
          {/* ST-2 — the walk: scan a blank, place it, photos then words. */}
          <Route path="/widget/tags" element={<SiteTagsWidget />} />
          
          {/* Financials - Back of the House */}
          <Route path="/financials" element={<FinancialsPage />} />
          <Route path="/financials/budgets" element={<BudgetTracker />} />
          <Route path="/financials/invoices" element={<InvoicesPage />} />
          <Route path="/financials/change-orders" element={<ChangeOrdersPage />} />
          <Route path="/financials/rfps" element={<RFPsPage />} />
          <Route path="/financials/insurance-audit" element={<InsuranceAuditPage />} />
          
          {/* Client Portal - The Drawing Room */}
          <Route path="/drawing-room/share" element={<DreamShare />} />
          <Route path="/drawing-room/:accessToken" element={<DrawingRoom />} />
          
          {/* Contractor Portal - Site Office */}
          <Route path="/site-office/:token" element={<SiteOfficePage />} />
          {/* SO-3a — the party's punch list, and one item on its own page. */}
          <Route path="/site-office/:token/punch" element={<SiteOfficePunchItem />} />
          <Route path="/site-office/:token/punch/:itemId" element={<SiteOfficePunchItem />} />
          {/* SO-3b — every visit, and the party's own progress photo. */}
          <Route path="/site-office/:token/photos" element={<SiteOfficePhotos />} />
          {/* SO-3c — the party's spec sheets, and one sheet with its Trade PDF. */}
          <Route path="/site-office/:token/specs" element={<SiteOfficeSpecs />} />
          <Route path="/site-office/:token/specs/:clipId" element={<SiteOfficeSpecItem />} />
          {/* RS-2 — the party's questions, and the way to ask one. */}
          <Route path="/site-office/:token/questions" element={<OfficeQuestions />} />
          {/* RS-5 — the Submittals door, beside Questions. */}
          <Route path="/site-office/:token/submittals" element={<OfficeSubmittals />} />
          {/* DP275-P4a — the scanned field sheet: no login, no shell. */}
          <Route path="/f/:code" element={<FieldSheetPage />} />
          <Route path="/m/:code" element={<MaterialTagPage />} />
          {/* RS-3a — a palette a contractor is given: no login, no shell. */}
          <Route path="/p/:code" element={<PalettePublicPage />} />
          {/* RS-8 — a vendor's answer page: no login, no shell. */}
          <Route path="/q/:code" element={<QuestionAnswerPage />} />
          {/* AS-2d — the assembly a drawing block's QR opens: no login, no shell. */}
          <Route path="/a/:key" element={<AssemblyPublicPage />} />
          {/* CS-2 — a shared Spec Sheet, read logged out from its token. */}
          <Route path="/s/:token" element={<PublicSpecSheet />} />
          <Route path="/r/:token" element={<PublicPlanReview />} />
          {/* CS-3b — a shared Spec Book: the token in the link is the credential */}
          <Route path="/b/:token" element={<PublicSchedulePage />} />

          {/* DP269-A — staff "Open as …" from the team row. Same page, admin payload. */}
          <Route path="/projects/:projectId/site-office/:membershipId" element={<SiteOfficePage />} />

          {/* DP268-E — the agreement a party signs from the emailed link. No login. */}
          <Route path="/agreement/:token" element={<AgreementPage />} />
          
          {/* Master Contractor Portal */}
          <Route path="/contractor-portal" element={<ContractorPortal />} />
          
          {/* Legal Pages - Standalone, view-only */}
          <Route path="/legal/:pageType" element={<LegalPage />} />
          
          {/* Pipeline - Unified Leads & CRM */}
          <Route path="/pipeline" element={<PipelinePage />} />
          <Route path="/pipeline/:leadId" element={<LeadDetailPage />} />
          <Route path="/consultation/:leadId" element={<ConsultationPage />} />
          <Route path="/leads" element={<Navigate to="/pipeline" replace />} />
          <Route path="/crm" element={<Navigate to="/pipeline" replace />} />
          
          {/* Notes */}
          <Route path="/notes" element={<NotesPage />} />
          
          {/* Contractors - Back Office */}
          <Route path="/contractors" element={<ContractorsPage />} />
          
          {/* Vendors & Purchase Orders */}
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="/procurement" element={<ProcurementDashboard />} />
          {/* ST-1 — the firm's own numbered blanks. */}
          <Route path="/site-tags" element={<SiteTagsPage />} />
          {/* ST-3a — the tag's own page. */}
          <Route path="/site-tags/:tagId" element={<SiteTagPage />} />
          
          {/* Work Tables */}
          <Route path="/work-table" element={<WorkTableHome />} />
          <Route path="/work-tables" element={<WorkTableHome />} />
          <Route path="/work-tables/:workTableId" element={<WorkTablePage />} />
          <Route path="/qr-codes" element={<QRCodeGenerator />} />
          <Route path="/work-tables/shared/:shareToken" element={<WorkTablePage />} />
          
          {/* Portfolio Viewer (public) */}
          <Route path="/portfolio/:token" element={<PortfolioViewer />} />

          {/* DP263 — a single shared site-visit photo (public, no chrome) */}
          <Route path="/photo-shares/:token" element={<PhotoShareViewer />} />

          {/* DP262 — a memo shared outside the app (public, no chrome) */}
          <Route path="/memo/:token" element={<MemoShareViewer />} />
          
          {/* Corner of the Desk - Personal Space */}
          <Route path="/corner" element={<CornerOfDesk />} />
          
          {/* Knowledge Base */}
          <Route path="/knowledge" element={<KnowledgeBasePage />} />
          <Route path="/knowledge/:itemId" element={<KnowledgeBasePage />} />
          
          {/* Design Companion (app-wide, no project required) */}
          <Route path="/design-companion" element={<DesignCompanionPage />} />
          
          {/* Clips */}
          <Route path="/clips" element={<ClipsLibrary />} />
          <Route path="/clips/:clipId" element={<ClipDetail />} />
          
          {/* Survey Templates */}
          <Route path="/survey-templates" element={<SurveyTemplatesPage />} />
          
          {/* Scheduled Emails Dashboard */}
          <Route path="/scheduled-emails" element={<ScheduledEmailsPage />} />
          
          {/* Projects */}
          <Route path="/projects/:projectId" element={<ProjectRedirect />} />
          <Route path="/projects/:projectId/hub" element={<HubLegacyRedirect />} />
          <Route path="/projects/:projectId/desk" element={<ProjectHubFull />} />
          <Route path="/projects/:projectId/documents" element={<DocumentGenerator />} />
          <Route path="/projects/:projectId/estimates/new" element={<EstimatePage />} />
          <Route path="/projects/:projectId/estimates/:estimateId" element={<EstimatePage />} />
          {/* CS-3a/CS-3b — one builder route: "new" is an unsaved Spec Book,
              any other id is the saved one. */}
          <Route path="/projects/:projectId/schedules/:scheduleId" element={<ScheduleBuilderPage />} />
          <Route path="/projects/:projectId/mood-boards/new" element={<MoodBoardPage />} />
          <Route path="/projects/:projectId/mood-boards/:moodBoardId" element={<MoodBoardPage />} />
          <Route path="/projects/:projectId/surveys/new" element={<ClientSurveyPage />} />
          <Route path="/projects/:projectId/surveys/:surveyId" element={<ClientSurveyPage />} />
          <Route path="/projects/:projectId/scope-statements/new" element={<ScopeStatementEditor />} />
          <Route path="/projects/:projectId/scope-statements/:statementId" element={<ScopeStatementEditor />} />
          <Route path="/projects/:projectId/process-map" element={<ProcessMapPage />} />
          {/* Wave 5.8 — My Desk cross-project Plan Room window. Per-project
              register stays at /projects/:id/drawings above. */}
          <Route path="/plan-room" element={<MyDeskPlanRoom />} />
          <Route path="/projects/:projectId/ask-for-a-price" element={<AskForPricePage />} />
          {/* Wave 3 (2026-02) — Route rename: canonical URL is now
              /punch-lists. Legacy /quality-control still resolves to
              the same page for any bookmarks/emails predating the
              rename. Component internally re-labels to "Punch Lists". */}
          <Route path="/projects/:projectId/quality-control" element={<QualityControlPage />} />
          {/* DP278-C6c-2 — the standalone project pages, under one parent that
              draws the page's nudge line above its header. Paths, elements and
              order are exactly as they were declared. */}
          <Route element={<ProjectRouteNudge />}>
            <Route path="/projects/:projectId/punch-list" element={<PunchListPage />} />
            <Route path="/projects/:projectId/decisions" element={<DecisionLogPage />} />
            <Route path="/projects/:projectId/purchases" element={<PurchasesRoute />} />
            <Route path="/projects/:projectId/rfps" element={<RFPsPage />} />
            <Route path="/projects/:projectId/draw-requests" element={<DrawRequestsPage />} />
            <Route path="/projects/:projectId/received-invoices" element={<ReceivedInvoicesPage />} />
            <Route path="/projects/:projectId/purchase-orders" element={<PurchaseOrdersPageWrapper />} />
            <Route path="/projects/:projectId/gallery" element={<ProjectGallery />} />
            <Route path="/projects/:projectId/design-companion" element={<DesignCompanionPage />} />
            <Route path="/projects/:projectId/floor-plans" element={<FloorPlanPage />} />
            <Route path="/projects/:projectId/room-takeoff" element={<Navigate to="../floor-plans" relative="path" replace />} />
            <Route path="/projects/:projectId/bids" element={<ProjectProcurement />} />
            <Route path="/projects/:projectId/drawings" element={<DrawingControlPage />} />
            <Route path="/projects/:projectId/rfis" element={<RFISubmittalPage />} />
            <Route path="/projects/:projectId/bid-packages" element={<BidPackagesPage />} />
            <Route path="/projects/:projectId/scope-builder" element={<ScopeBuilderPage />} />
            <Route path="/projects/:projectId/punch-lists" element={<QualityControlPage />} />
            <Route path="/projects/:projectId/warranties" element={<WarrantyTrackerPage />} />
            <Route path="/projects/:projectId/permits" element={<PermitInspectionPage />} />
            <Route path="/projects/:projectId/lien-waivers" element={<LienWaiverPage />} />
          </Route>
          
          {/* Contacts */}
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/review-queue" element={<ContactReviewQueuePage />} />
          
          {/* Multi-Monitor Pop-out Routes */}
          <Route path="/popout/:type" element={<PopoutWrapper />} />
          <Route path="/popout/:type/:projectId" element={<PopoutWrapper />} />
          <Route path="/popout/:type/:projectId/:toolId" element={<PopoutWrapper />} />
        </Routes>
        </PublicOrAuthGate>
        </Suspense>
        <HideOnLegalPages>
          <BlueprintViewer />
          <div className="hidden sm:block"><GlobalBlueprintToggle darkMode={darkMode} /></div>
          <PWAInstallPrompt />
        </HideOnLegalPages>
        </TourProvider>
        <AiChadWrapper darkMode={darkMode} />
        <GlobalFixedNav darkMode={darkMode} />
        {/* DP106 Part 1b — MY PROFILE OPENS FROM EVERY PAGE. The account menu
            is shell furniture, so its dialog is mounted here, once, beside the
            menu itself, and opens on the `km:open-profile` event. It was held
            in UserDashboard alone. */}
        <ProfileDialog darkMode={darkMode} />
        <UploadQueueIndicator />
        <HideOnLegalPages>
          <GlobalFooter darkMode={darkMode} />
        </HideOnLegalPages>
      </BrowserRouter>
      <Toaster position="top-right" />
      <BuildVersionWatcher />
    </div>
    </FocusSessionProvider>
    </HelpIconProvider>
    </BlueprintProvider>
    </WatermarkProvider>
    </RoleProvider>
    </AppErrorBoundary>
  );
}


// PR-2b — the Purchases page inside the project frame
function PurchasesRoute() {
  const { projectId } = useParams();
  return <ProjectFrame projectId={projectId} activeRoute="/projects/:projectId/purchases" here="Purchases"><PurchasesPage /></ProjectFrame>;
}

export default App;
// deploy-sim 1785094106951275455
